import { useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { useUsuarios, useUsuario } from '../hooks/useUsuario'
import { UsuarioFilters, Usuario, UsuarioFormData } from '../tipos/usuario'
import { UsuarioFiltersComponent } from '../componentes/FiltrosUsuario'
import { UsuarioList } from '../componentes/ListaUsuario'
import { FormularioUsuario } from '../componentes/FormularioUsuario'

const convertUsuarioToFormData = (usuario: Usuario | null): UsuarioFormData | undefined => {
  if (!usuario) return undefined

  return {
    id: usuario.id,
    nome: usuario.nome,
    email: usuario.email,
    senha: '',
    confirmarSenha: '',
    ativo: usuario.ativo,
    pessoaId: usuario.pessoaId ? String(usuario.pessoaId) : '',
    papelCodigo: usuario.papelCodigo !== undefined ? String(usuario.papelCodigo) : '',
  }
}

function Usuarios() {
  const [filters, setFilters] = useState<UsuarioFilters>({
    search: '',
    ativo: 'todos',
    papelCodigo: 'todos',
  })
  const [showForm, setShowForm] = useState(false)
  const [editingUsuario, setEditingUsuario] = useState<Usuario | null>(null)
  const [editingUsuarioId, setEditingUsuarioId] = useState<string | null>(null)

  const { data: usuarios = [], isLoading, refetch } = useUsuarios(filters)
  const {
    data: usuarioCompleto,
    isLoading: isLoadingUsuario,
  } = useUsuario(editingUsuarioId ?? undefined)

  const usuarioParaEdicao = useMemo(() => {
    if (usuarioCompleto) return usuarioCompleto
    return convertUsuarioToFormData(editingUsuario)
  }, [usuarioCompleto, editingUsuario])

  const handleSearch = (newFilters: UsuarioFilters) => {
    setFilters(newFilters)
  }

  const handleCreateUsuario = () => {
    setEditingUsuario(null)
    setEditingUsuarioId(null)
    setShowForm(true)
  }

  const handleEditUsuario = (usuario: Usuario) => {
    setEditingUsuario(usuario)
    setEditingUsuarioId(usuario.id || null)
    setShowForm(true)
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingUsuario(null)
    setEditingUsuarioId(null)
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuários</h1>
          <p className="mt-1 text-sm text-gray-600">Gerencie quem pode acessar o sistema e seus respectivos papéis.</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={handleCreateUsuario}
            className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 shadow-theme-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-2 inline" />
            Novo Usuário
          </button>
        </div>
      </div>

      <UsuarioFiltersComponent onSearch={handleSearch} />

      <UsuarioList usuarios={usuarios} isLoading={isLoading} onRefresh={refetch} onEditUsuario={handleEditUsuario} />

      <FormularioUsuario
        isOpen={showForm}
        onClose={handleCloseForm}
        usuario={usuarioParaEdicao}
        isEditing={!!editingUsuarioId}
      />

      {isLoadingUsuario && editingUsuarioId && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
            <div className="loading-spinner w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-700">Carregando dados do usuário...</span>
          </div>
        </div>
      )}
    </>
  )
}

export default Usuarios

